import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AssetSearchResult } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import AdminLayout from '@/components/Layout/AdminLayout';
import { showToast } from '@/components/Layout/Toast';
import { Button, StatusBadge } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import CallLabelEditor from './CallLabelEditor';
import ScoreSummary from './ScoreSummary';
import {
    actionChoices,
    biasChoices,
    draftProblems,
    formatEastern,
    instrumentKey,
    labelOf,
    statementKindChoices,
    toDraft,
    type DraftCall,
    type InstrumentDisplay,
    type PostReview,
    type ReviewQueueItem
} from './callReviewModel';

type TriageReading = NonNullable<NonNullable<ReviewQueueItem['pipeline']>['triage']>;

/** How the retired topic filter's topics read on the page. */
const topicLabels: Record<NonNullable<TriageReading['topic']>, string> = {
    specific_instrument: 'a specific instrument',
    general_market: 'the general market',
    trading_education: 'trading education',
    unrelated: 'unrelated'
};

/** A triage decision in words: the claim gate's probability, or the retired topic filter's topic. */
const triageLabel = (triage: TriageReading): string => {
    if (triage.claimProbability !== null) return `claim gate ${Math.round(triage.claimProbability * 100)}%`;
    return triage.topic ? `topic: ${topicLabels[triage.topic]}` : triage.decision;
};

/** What the pipeline did with a labelled post, in the queue's words. */
const pipelineLabel = (pipeline: NonNullable<ReviewQueueItem['pipeline']>): string => {
    if (pipeline.triage?.decision === 'ignore' && pipeline.activeClaimCount === null) {
        return `filtered out (${triageLabel(pipeline.triage)})`;
    }
    if (pipeline.processingStatus === 'quarantined') return 'quarantined';
    if (pipeline.processingStatus === 'failed') return 'failed';
    if (pipeline.activeClaimCount) return `${pipeline.activeClaimCount} call${pipeline.activeClaimCount === 1 ? '' : 's'}`;
    return 'no calls';
};

const queueBadge = (item: ReviewQueueItem) => {
    if (item.needsRelabel) return <StatusBadge status="relabel" tone="warning" />;
    return <StatusBadge status={item.labelled ? 'labelled' : 'to label'} tone={item.labelled ? 'success' : 'neutral'} />;
};

/**
 * Blind hand labelling of the X call-extraction holdout: for each post, record the calls it really contains before
 * seeing what the pipeline found. The answer key scores this pipeline and any future prompt or model.
 */
const IntelCallReview = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = Number(searchParams.get('documentId')) || null;
    const [showLabelled, setShowLabelled] = useState(false);

    const queue = useAppQuery(trpc.intelReview.listQueue.queryOptions(), {
        fallbackErrorMessage: 'Failed to load the review queue',
        showErrorToast: true
    });
    const score = useAppQuery(trpc.intelReview.score.queryOptions(), {
        fallbackErrorMessage: 'Failed to load the score',
        showErrorToast: true
    });

    const items = useMemo(
        () => (queue.data?.items ?? []).filter((item) => showLabelled || !item.labelled || item.documentId === selectedId),
        [queue.data, showLabelled, selectedId]
    );
    const select = (documentId: number) => setSearchParams({ documentId: String(documentId) });

    useEffect(() => {
        const firstOpen = queue.data?.items.find((item) => !item.labelled);
        if (!selectedId && firstOpen) setSearchParams({ documentId: String(firstOpen.documentId) }, { replace: true });
    }, [queue.data, selectedId, setSearchParams]);

    const nextUnlabelled = (afterId: number): number | null => {
        const all = queue.data?.items ?? [];
        const start = all.findIndex((item) => item.documentId === afterId);
        const after = [...all.slice(start + 1), ...all.slice(0, Math.max(start, 0))];
        return after.find((item) => !item.labelled && item.documentId !== afterId)?.documentId ?? null;
    };

    return (
        <AdminLayout containerSize="1600px" title="X call review">
            <div className="space-y-4 text-slate-100">
                <p className="text-sm text-slate-400">
                    Label what each holdout post really says. If it has no call, choose “No calls in this post”. If it has one, choose “Yes,
                    add the call”, enter it, and save; a post is recorded as having a call only once the call is entered. Only absolute
                    price levels are labelled. The pipeline’s answers stay hidden until a post is labelled, so the first labels are blind;
                    changing labels after seeing them takes the post out of the blind score.
                </p>
                {score.data && <ScoreSummary score={score.data} />}
                <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
                    <aside className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">
                                {queue.data ? `${queue.data.labelledPosts} of ${queue.data.holdoutPosts} labelled` : 'Loading…'}
                            </span>
                            <label className="flex items-center gap-2 text-slate-300">
                                <input checked={showLabelled} onChange={(event) => setShowLabelled(event.target.checked)} type="checkbox" />
                                Show labelled
                            </label>
                        </div>
                        <ul className="max-h-[75vh] space-y-1 overflow-y-auto pr-1">
                            {items.map((item) => (
                                <li key={item.documentId}>
                                    <button
                                        className={`w-full rounded border px-3 py-2 text-left text-sm ${
                                            item.documentId === selectedId
                                                ? 'border-sky-600 bg-slate-800'
                                                : 'border-slate-800 bg-slate-900 hover:bg-slate-800'
                                        }`}
                                        onClick={() => select(item.documentId)}
                                        type="button"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-semibold">@{item.handle}</span>
                                            {queueBadge(item)}
                                        </div>
                                        <div className="mt-1 flex justify-between text-xs text-slate-400">
                                            <span>{item.publishedAt === null ? '—' : formatEastern(item.publishedAt)}</span>
                                            {item.pipeline && <span>{pipelineLabel(item.pipeline)}</span>}
                                        </div>
                                    </button>
                                </li>
                            ))}
                            {queue.data && items.length === 0 && (
                                <li className="text-sm text-slate-400">Every holdout post is labelled.</li>
                            )}
                        </ul>
                    </aside>
                    <main>
                        {selectedId ? (
                            <PostPanel
                                documentId={selectedId}
                                onSaved={() => {
                                    const next = nextUnlabelled(selectedId);
                                    if (next) select(next);
                                }}
                            />
                        ) : (
                            <p className="text-sm text-slate-400">Choose a post.</p>
                        )}
                    </main>
                </div>
            </div>
        </AdminLayout>
    );
};

const PostPanel = ({ documentId, onSaved }: { documentId: number; onSaved: () => void }) => {
    const post = useAppQuery(trpc.intelReview.getPost.queryOptions({ documentId }), {
        fallbackErrorMessage: 'Failed to load the post',
        showErrorToast: true
    });
    if (post.isLoading) return <DataLoadingSpinner />;
    if (!post.data) return <p className="text-sm text-red-300">{post.error?.message ?? 'Post not found'}</p>;
    // Keyed by post so the draft starts fresh for every post.
    return <PostReviewForm key={documentId} onSaved={onSaved} post={post.data} />;
};

const PostReviewForm = ({ post, onSaved }: { post: PostReview; onSaved: () => void }) => {
    const queryClient = useQueryClient();
    const [calls, setCalls] = useState<DraftCall[]>(() => (post.labels ?? []).map(toDraft));
    const [instruments, setInstruments] = useState(
        () => new Map<string, InstrumentDisplay>(post.instruments.map((item) => [instrumentKey(item), item]))
    );
    const [unpicked, setUnpicked] = useState(() => new Set<string>());

    const save = useMutation(
        trpc.intelReview.saveLabels.mutationOptions({
            onError: (error) => showToast(error.message, 'error'),
            onSuccess: async () => {
                showToast('Labels saved', 'success');
                await Promise.all([
                    queryClient.invalidateQueries(trpc.intelReview.listQueue.pathFilter()),
                    queryClient.invalidateQueries(trpc.intelReview.score.pathFilter()),
                    queryClient.invalidateQueries(trpc.intelReview.getPost.pathFilter())
                ]);
                onSaved();
            }
        })
    );

    const problems = [...(unpicked.size ? ['Choose an instrument for every added call'] : []), ...draftProblems(calls)];
    const submit = (labelled: DraftCall[]) =>
        save.mutate({
            documentId: post.documentId,
            predictionsShown: post.predictions !== null,
            // The context shown with the post; a later deletion of it invalidates exactly these labels.
            context: {
                repliedToDocumentId: post.repliedTo?.documentId ?? null,
                quotedDocumentId: post.quoted?.documentId ?? null
            },
            calls: labelled.map(({ key: _key, ...call }) => call)
        });

    const pickInstrument = (call: DraftCall, result: AssetSearchResult) => {
        const instrumentForm =
            result.type === 'Cryptocurrency'
                ? 'crypto'
                : result.type === 'AssetIndex'
                  ? call.instrumentForm === 'option'
                      ? 'option'
                      : 'index'
                  : call.instrumentForm === 'crypto' || call.instrumentForm === 'index'
                    ? 'stock'
                    : call.instrumentForm;
        setInstruments((current) =>
            new Map(current).set(`${result.type}:${result.id}`, {
                entityType: result.type,
                entityId: result.id,
                symbol: result.symbol,
                name: result.name
            })
        );
        setCalls((current) =>
            current.map((item) =>
                item.key === call.key
                    ? {
                          ...item,
                          entityType: result.type,
                          entityId: result.id,
                          instrumentForm,
                          optionType: instrumentForm === 'option' ? (item.optionType ?? 'call') : null
                      }
                    : item
            )
        );
        setUnpicked((current) => {
            const next = new Set(current);
            next.delete(call.key);
            return next;
        });
    };

    const addCall = () => {
        const draft = toDraft({
            entityType: 'Asset',
            entityId: 1,
            instrumentForm: 'stock',
            optionType: null,
            statementKind: 'forecast',
            underlyingBias: null,
            positionSide: null,
            action: 'none',
            statedLifecycle: 'none',
            horizonClass: 'unspecified',
            claimedEntryPrice: null,
            targets: []
        });
        setCalls((current) => [...current, draft]);
        setUnpicked((current) => new Set(current).add(draft.key));
    };

    return (
        <div className="space-y-4">
            <article className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="font-semibold">@{post.handle}</span>
                    <span className="text-slate-400">{formatEastern(post.publishedAt)} ET</span>
                    {post.url && (
                        <a className="text-sky-400 hover:underline" href={post.url} rel="noreferrer" target="_blank">
                            Open on X
                        </a>
                    )}
                </div>
                <p className="mt-3 whitespace-pre-wrap text-base text-slate-100">{post.text}</p>
                {post.hasMedia && <p className="mt-2 text-xs text-amber-300">Has images or video (open on X to see them).</p>}
                {[
                    ['Replies to', post.repliedTo],
                    ['Quotes', post.quoted]
                ].map(([label, context]) =>
                    context && typeof context === 'object' ? (
                        <blockquote className="mt-3 border-l-2 border-slate-600 pl-3 text-sm text-slate-300" key={String(label)}>
                            <p className="text-xs text-slate-500">
                                {String(label)} {context.handle ? `@${context.handle}` : 'another account'} ·{' '}
                                {formatEastern(context.publishedAt)} ET
                            </p>
                            <p className="whitespace-pre-wrap">{context.text}</p>
                        </blockquote>
                    ) : null
                )}
            </article>

            {post.predictions && <Predictions instruments={instruments} predictions={post.predictions} />}

            {post.needsRelabel && (
                <p className="rounded border border-amber-700 bg-amber-950/40 p-3 text-sm text-amber-200" role="status">
                    This post’s earlier labels were withdrawn because a post it replies to or quotes was deleted. Label it again from what
                    is shown here.
                </p>
            )}

            <section className="space-y-3">
                <p className="text-slate-300">
                    {post.labels === null ? 'Not labelled yet' : 'Labelled'} · {calls.length} call
                    {calls.length === 1 ? '' : 's'}
                </p>
                <CallLabelEditor
                    calls={calls}
                    instruments={instruments}
                    onChange={setCalls}
                    onInstrumentPicked={pickInstrument}
                    onRemove={(call) => {
                        setCalls((current) => current.filter((item) => item.key !== call.key));
                        setUnpicked((current) => {
                            const next = new Set(current);
                            next.delete(call.key);
                            return next;
                        });
                    }}
                    unpicked={unpicked}
                />
                {post.predictions && (
                    <p className="text-xs text-slate-500">
                        The pipeline’s answers are on screen, so saving changes here takes this post out of the blind score.
                    </p>
                )}
                {problems.length > 0 && (
                    <ul className="list-disc pl-5 text-sm text-amber-300" role="alert">
                        {problems.map((problem) => (
                            <li key={problem}>{problem}</li>
                        ))}
                    </ul>
                )}
                {calls.length === 0 ? (
                    // Nothing entered yet: the only choices are "no call" and "enter the call", so an empty save
                    // can never stand in for a post that has one.
                    <div className="space-y-2 rounded-lg border border-slate-700 bg-slate-900/60 p-4">
                        <p className="text-sm text-slate-200">Does the author make a call in this post?</p>
                        <div className="flex flex-wrap gap-2">
                            <Button isLoading={save.isPending} onClick={() => submit([])} variant="secondary">
                                No calls in this post
                            </Button>
                            <Button disabled={save.isPending} onClick={addCall} variant="primary">
                                Yes, add the call
                            </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                            A post with a call is saved only after you enter it: the instrument, the direction, and what the author did or
                            recommends.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        <Button disabled={problems.length > 0} isLoading={save.isPending} onClick={() => submit(calls)} variant="primary">
                            Save {calls.length} call{calls.length === 1 ? '' : 's'} and next
                        </Button>
                        <Button disabled={save.isPending} onClick={addCall} variant="secondary">
                            Add another call
                        </Button>
                    </div>
                )}
            </section>
        </div>
    );
};

/** What the pipeline made of the post, shown only once the post has labels. */
const Predictions = ({
    predictions,
    instruments
}: {
    predictions: NonNullable<PostReview['predictions']>;
    instruments: Map<string, InstrumentDisplay>;
}) => {
    const run = predictions.runStatus;
    return (
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-4 text-sm">
            <p className="mb-2 text-slate-300">What the pipeline found</p>
            {predictions.modelCalls.length > 0 ? (
                <ul className="space-y-2">
                    {predictions.modelCalls.map((call) => (
                        <li className="text-slate-300" key={call.eventId}>
                            <span className="font-semibold">{instruments.get(instrumentKey(call))?.symbol ?? instrumentKey(call)}</span> ·{' '}
                            {labelOf(statementKindChoices, call.statementKind)} · {labelOf(biasChoices, call.underlyingBias ?? '')} ·{' '}
                            {labelOf(actionChoices, call.action)}
                            <span className="block text-xs text-slate-500">
                                “{call.evidenceQuote}”{call.evidenceSource === 'image' ? ' (read from an image)' : ''} · confidence{' '}
                                {call.extractionConfidence}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-slate-400">No calls.</p>
            )}
            <p className="mt-3 text-xs text-slate-500">
                Triage: {predictions.triage ? triageLabel(predictions.triage) : 'not run'} · extraction{' '}
                {run
                    ? `${run.runStatus}${run.errorCode ? ` (${run.errorCode})` : ''}, ${run.modelKey}, prompt v${run.promptVersion}`
                    : 'not run'}
            </p>
        </section>
    );
};

export default IntelCallReview;
